import { useEffect, useState } from "react";

import { type FetchState, fetchJson, NotFoundError, seg } from "./api";

/**
 * The public station status payload, `GET /api/v1/station/:station/status`.
 *
 * Hand-written rather than generated: the homepage ships no API client (the
 * dashboard owns that), and this one endpoint is all the public station page
 * needs. Mirrors the StationStatus schema in openapi.yaml — only the fields
 * the page renders are declared.
 */

export interface StationArtist {
  id: string;
  name: string;
}

export interface StationTrack {
  id: string;
  name: string;
  artist?: StationArtist | null;
  album?: StationArtist | null;
}

export interface StationShow {
  id: string;
  /** URL segment of the show's public page; empty for externally-sourced shows. */
  slug?: string;
  name: string;
  description?: string;
  url?: string;
  image_url?: string;
}

export interface StationEvent {
  id: string;
  event_type: string;
  date_created: string;
  show?: StationShow | null;
  track?: StationTrack | null;
}

export interface StationStatus {
  id: string;
  slug?: string;
  name: string;
  description?: string;
  homepage_url?: string;
  logo_url?: string;
  stream_url: string;
  current_track?: StationTrack | null;
  current_show?: StationShow | null;
  events: StationEvent[];
}

/** `event_type` of the events the status panel reads. */
export const EVENT_TRACK_STARTED = "track-started";

export const fetchStationStatus = (station: string, signal?: AbortSignal) =>
  fetchJson<StationStatus>(`station/${seg(station)}/status`, signal);

/** How often the station page re-reads the status payload. */
export const POLL_INTERVAL_MS = 20_000;

/**
 * The same states every public page has (see lib/api), with one wrinkle: a
 * failed *refresh* keeps the last good payload on screen, so "error" only
 * ever means the very first read failed.
 */
export type StationStatusState = FetchState<StationStatus>;

/**
 * Polls a station's status while the tab is visible.
 *
 * Backgrounded tabs stop polling and read once more on return, so a page left
 * open overnight neither hammers the API nor shows last night's track.
 */
export const useStationStatus = (
  station: string,
  intervalMs: number = POLL_INTERVAL_MS,
): StationStatusState => {
  const [state, setState] = useState<StationStatusState>({ kind: "loading" });

  useEffect(() => {
    setState({ kind: "loading" });

    let disposed = false;
    let timer: ReturnType<typeof setInterval> | null = null;
    let inFlight = false;
    const controller = new AbortController();

    const stopPolling = () => {
      if (timer !== null) {
        clearInterval(timer);
        timer = null;
      }
    };

    const load = async () => {
      // Never cancel a read that's already running: the server is midway
      // through it (possibly waiting on the station's metadata provider), and
      // hanging up just makes it do that work for nothing. Skip this tick and
      // take the next one instead.
      if (inFlight) {
        return;
      }
      inFlight = true;
      try {
        const status = await fetchStationStatus(station, controller.signal);
        if (!disposed) {
          setState({ kind: "ready", data: status });
        }
      } catch (error) {
        if (disposed || (error as Error)?.name === "AbortError") {
          return;
        }
        if (error instanceof NotFoundError) {
          stopPolling();
          setState({ kind: "missing" });
          return;
        }
        // A dropped refresh leaves the last good payload on screen; only a
        // cold start has nothing better to show than the error.
        setState((previous) =>
          previous.kind === "ready" ? previous : { kind: "error" },
        );
      } finally {
        inFlight = false;
      }
    };

    const startPolling = () => {
      stopPolling();
      timer = setInterval(load, intervalMs);
    };

    const onVisibilityChange = () => {
      if (document.hidden) {
        stopPolling();
      } else if (!disposed) {
        load();
        startPolling();
      }
    };

    load();
    startPolling();
    document.addEventListener("visibilitychange", onVisibilityChange);

    return () => {
      disposed = true;
      stopPolling();
      controller.abort();
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, [station, intervalMs]);

  return state;
};

/** "just now" / "12m ago" / "3h ago" / "2d ago". */
export const relativeTime = (iso: string, now: number = Date.now()): string => {
  const elapsedMinutes = Math.floor((now - new Date(iso).getTime()) / 60_000);
  if (!Number.isFinite(elapsedMinutes) || elapsedMinutes < 1) {
    return "just now";
  }
  if (elapsedMinutes < 60) {
    return `${elapsedMinutes}m ago`;
  }
  const hours = Math.floor(elapsedMinutes / 60);
  if (hours < 24) {
    return `${hours}h ago`;
  }
  return `${Math.floor(hours / 24)}d ago`;
};

/**
 * The station's recently-played tracks, newest first.
 *
 * `events` mixes in chat and presence; this keeps the track-started ones,
 * drops repeats of the same track back-to-back (stations re-announce a long
 * track), and skips whatever is playing right now, which the player itself
 * already shows. The API hands them over newest-first already; sorting on
 * `date_created` states that rather than depending on it.
 */
export const recentTracks = (
  status: StationStatus,
  limit: number,
): { id: string; track: StationTrack; playedAt: string }[] => {
  const newestFirst = [...status.events].sort(
    (a, b) =>
      new Date(b.date_created).getTime() - new Date(a.date_created).getTime(),
  );

  const played: { id: string; track: StationTrack; playedAt: string }[] = [];
  let previousTrackId = status.current_track?.id;
  for (const event of newestFirst) {
    if (event.event_type !== EVENT_TRACK_STARTED || !event.track) {
      continue;
    }
    if (event.track.id === previousTrackId) {
      continue;
    }
    previousTrackId = event.track.id;
    played.push({
      id: event.id,
      track: event.track,
      playedAt: event.date_created,
    });
    if (played.length === limit) {
      break;
    }
  }
  return played;
};
